import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHeaderIndicatorsQuery } from '../../hooks/queries';

export function SlaBreachIndicator() {
  const navigate = useNavigate();
  const { data } = useHeaderIndicatorsQuery();
  const [isTransientPulseActive, setIsTransientPulseActive] = useState(false);
  const previousSlaBreachCountRef = useRef(0);
  const transientPulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const slaBreachCount = data?.slaBreachCount ?? 0;
  const slaWarningCount = data?.slaWarningCount ?? 0;
  const totalCount = slaBreachCount + slaWarningCount;
  const assignedToMeBreachedCount = data?.assignedToMeBreachedIds.length ?? 0;
  const targetIncidentId = useMemo(() => {
    const breachedIds = data?.slaBreachedIncidentIds ?? [];
    const warningIds = data?.slaWarningIncidentIds ?? [];

    if (totalCount !== 1) {
      return null;
    }

    if (breachedIds.length === 1) {
      return breachedIds[0];
    }

    if (warningIds.length === 1) {
      return warningIds[0];
    }

    return null;
  }, [data?.slaBreachedIncidentIds, data?.slaWarningIncidentIds, totalCount]);

  useEffect(() => {
    const previousSlaBreachCount = previousSlaBreachCountRef.current;
    const hasIncreased = slaBreachCount > previousSlaBreachCount;

    if (hasIncreased) {
      setIsTransientPulseActive(true); // eslint-disable-line react-hooks/set-state-in-effect -- intentional: pulse on prop change

      if (transientPulseTimerRef.current) {
        clearTimeout(transientPulseTimerRef.current);
      }

      transientPulseTimerRef.current = setTimeout(() => {
        setIsTransientPulseActive(false);
        transientPulseTimerRef.current = null;
      }, 3000);
    }

    previousSlaBreachCountRef.current = slaBreachCount;
  }, [slaBreachCount]);

  useEffect(() => {
    return () => {
      if (transientPulseTimerRef.current) {
        clearTimeout(transientPulseTimerRef.current);
      }
    };
  }, []);

  if (totalCount <= 0) {
    return null;
  }

  const hasBreach = slaBreachCount > 0;
  const colorClasses = hasBreach
    ? 'text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300'
    : 'text-yellow-500 hover:text-yellow-600 dark:text-yellow-400 dark:hover:text-yellow-300';
  const shouldPulse = isTransientPulseActive || assignedToMeBreachedCount > 0;

  const handleClick = () => {
    if (targetIncidentId) {
      navigate(`/incidents/${targetIncidentId}`);
      return;
    }

    navigate('/incidents?slaBreached=true');
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${colorClasses} ${
        shouldPulse ? 'animate-pulse' : ''
      }`}
      aria-label={`SLA indicators (${slaBreachCount} breached, ${slaWarningCount} warning)`}
      title={`SLA: ${slaBreachCount} breached, ${slaWarningCount} warning`}
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01M5.062 19h13.876c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.33 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">
        {totalCount > 99 ? '99+' : totalCount}
      </span>
    </button>
  );
}

